import { AlertTriangle, Check, Clock3, Undo2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useReopenOrder, useResolveOrder, type OrderDetail } from "../../services/use-orders"
import { formatDateTime } from "../../lib/format-date"
import { Button } from "../ui/button"
import { Callout } from "../ui/callout"
import { StateChip } from "../../routes/orders.index"
import { AddressCorrectionPanel } from "./address-correction"
import { InvestigationNote } from "./order-investigation"

type OrderStatusData = Pick<
  OrderDetail,
  "marketplace" | "orderId" | "state" | "resolution" | "problems" | "investigation"
> &
  Partial<Pick<OrderDetail, "addressCorrection">>

export function OrderStatusPanels({ data }: { data: OrderStatusData }) {
  const resolved = !!data.resolution.resolvedAt
  const hadResolution = useRef(resolved)
  const reopenRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (resolved && !hadResolution.current) {
      reopenRef.current?.focus()
    }
    hadResolution.current = resolved
  }, [resolved])
  return (
    <>
      {data.state === "blocked" ? (
        <Callout tone="info" icon={<Clock3 className="size-4" />} title="Blocked by Mimovrste">
          <p className="mt-2 text-sm">
            Mimovrste has blocked this order. Awaiting marketplace release.
          </p>
        </Callout>
      ) : null}

      {resolved || data.problems.length > 0 || data.addressCorrection ? (
        <section
          aria-label="Order action"
          className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card"
        >
          {resolved ? (
            <ResolvedSection data={data} buttonRef={reopenRef} />
          ) : (
            <ProblemSection data={data} />
          )}

          {data.investigation ? (
            <div className="px-4 pb-4 sm:px-5">
              <InvestigationNote investigation={data.investigation} />
            </div>
          ) : null}

          {!resolved && data.addressCorrection ? (
            <AddressCorrectionPanel correction={data.addressCorrection} />
          ) : null}

          {!resolved && data.problems.length > 0 ? <ResolveForm data={data} /> : null}
        </section>
      ) : null}
    </>
  )
}

function ProblemSection({ data }: { data: OrderStatusData }) {
  const critical = data.problems.some((problem) => problem.severity === "critical")
  return (
    <div className="p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <AlertTriangle
          className={critical ? "size-5 shrink-0 text-destructive" : "size-5 shrink-0 text-warning"}
        />

        <div>
          <p className="text-xs text-muted-foreground">Systems disagree</p>

          <h2 className="mt-1 text-base font-semibold">
            {data.problems.length === 1
              ? data.problems[0]?.detail
              : `${data.problems.length} problems on this order`}
          </h2>
        </div>
      </div>

      <ul className="mt-4 space-y-3">
        {data.problems.map((problem, index) => (
          <li key={`${problem.code}:${index}`} className="flex flex-wrap items-start gap-2 text-sm">
            <StateChip kind={problem.severity === "critical" ? "bad" : "warn"}>
              <AlertTriangle className="mr-1 size-3" />

              {problem.severity === "critical" ? "Critical" : "Warning"}
            </StateChip>

            {data.problems.length > 1 ? (
              <span className="min-w-0 flex-1">{problem.detail}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ResolveForm({ data }: { data: OrderStatusData }) {
  const resolve = useResolveOrder(data.marketplace, data.orderId)
  const [comment, setComment] = useState("")
  const [emptyError, setEmptyError] = useState(false)
  const textarea = useRef<HTMLTextAreaElement>(null)
  const submitted = useRef(false)
  const serverError = resolve.isError ? mutationError(resolve.error, "Failed to resolve.") : null
  const error = emptyError ? "Write what you did before resolving." : serverError
  function submit() {
    if (submitted.current || resolve.isPending || resolve.isSuccess) {
      return
    }
    if (!comment.trim()) {
      setEmptyError(true)
      textarea.current?.focus()
      return
    }
    submitted.current = true
    resolve.mutate(comment.trim(), {
      onError: () => {
        submitted.current = false
      },
    })
  }
  return (
    <form
      className="p-4 sm:p-5"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <label htmlFor="resolve-comment" className="text-sm font-semibold">
        Resolve this order
      </label>

      <p id="resolve-help" className="mt-1 text-xs text-muted-foreground">
        Say what you did. Required — your comment stays with this resolution.
      </p>

      <textarea
        ref={textarea}
        id="resolve-comment"
        value={comment}
        onChange={(event) => {
          setComment(event.target.value)
          setEmptyError(false)
        }}
        aria-invalid={!!error}
        aria-describedby={error ? "resolve-help resolve-error" : "resolve-help"}
        rows={3}
        disabled={resolve.isPending || resolve.isSuccess}
        placeholder="What did you do about it?"
        className="mt-3 min-h-[72px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />

      {error ? (
        <p id="resolve-error" role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="mt-3 flex justify-end">
        <Button
          type="submit"
          variant="primary"
          className="min-h-11 w-full sm:w-auto"
          disabled={resolve.isPending || resolve.isSuccess}
        >
          <Check className="size-4" />

          {resolve.isPending || resolve.isSuccess ? "Resolving…" : "Mark resolved"}
        </Button>
      </div>
    </form>
  )
}

function ResolvedSection({
  data,
  buttonRef,
}: {
  data: OrderStatusData
  buttonRef: React.RefObject<HTMLButtonElement | null>
}) {
  const reopen = useReopenOrder(data.marketplace, data.orderId)
  const { resolvedAt, resolvedBy, comment } = data.resolution
  return (
    <div className="p-4 sm:p-5">
      <div className="flex flex-wrap items-start gap-3">
        <Check className="size-5 text-success" />

        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-foreground">
            {`Resolved by ${resolvedBy ?? "—"} · ${resolvedAt ? formatDateTime(resolvedAt) : "—"}`}
          </p>

          <h2 className="mt-2 whitespace-pre-wrap text-base font-semibold">
            {comment ?? "Resolved"}
          </h2>
        </div>

        <button
          ref={buttonRef}
          type="button"
          onClick={() => reopen.mutate()}
          disabled={reopen.isPending}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border px-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
        >
          <Undo2 className="size-4" />

          {reopen.isPending ? "Reopening…" : "Reopen"}
        </button>
      </div>

      {reopen.isError ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {reopen.error instanceof Error ? reopen.error.message : "Failed to reopen."}
        </p>
      ) : null}

      {data.problems.length > 0 ? (
        <div className="mt-4 border-t border-border pt-4">
          <h3 className="text-xs font-medium text-muted-foreground">What was wrong</h3>

          <ul className="mt-2 space-y-2">
            {data.problems.map((problem, index) => (
              <li
                key={`${problem.code}:${index}`}
                className="flex gap-2 text-sm text-muted-foreground"
              >
                <Check className="mt-0.5 size-4 shrink-0 text-success" />

                {problem.detail}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function mutationError(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}
